<template>
  <OtpVerify
    ref="otpRef"
    :contact="contact"
    :force-loading="loading"
    :show-in-drawer="showInDrawer"
    :store-id="guestStore.guest?.storeId"
    @verified="($event) => handleVerified($event)"
  >
    <div
      class="text-grays-darkest flex w-full max-w-lg flex-col space-y-7 md:items-center"
    >
      <div :class="showInDrawer ? 'text-start' : 'text-center'">
        <h1 v-if="!showInDrawer" class="text-2xl font-semibold">
          Claim your eyewear benefit now
        </h1>
        <p class="mt-4 text-sm">
          Enter your mobile number or email below to send a one-time
          verification code associated with your account. Message and data rates
          may apply.
        </p>
      </div>
      <label class="relative w-full">
        <span class="text-grays-darkest text-sm font-semibold tracking-wide"
          >Mobile number or email</span
        >
        <div
          v-if="!isMounted"
          class="bg-grays-lighter mt-1.5 h-14 w-full animate-pulse rounded-lg"
        ></div>
        <input
          v-else
          v-model="contact"
          type="email"
          placeholder="Enter mobile number or email"
          class="border-grays-light placeholder:text-grays-medium mt-1.5 block h-14 w-full rounded-lg border text-base placeholder:text-sm"
        />
        <p class="text-grays-dark mt-2 text-sm">
          Unsure which phone number or email is linked to your account?
          <a
            href="mailto:redemption.support@zennioptical.com"
            target="_blank"
            class="underline underline-offset-2"
          >
            Contact us at redemption.support@zennioptical.com</a
          >.
        </p>
      </label>
      <div
        v-if="!isMounted"
        class="bg-grays-lighter h-5 w-full animate-pulse rounded-lg"
      ></div>
      <div v-else class="flex self-start">
        <ZnCheckbox
          data-test-id="welcome-otp-agree"
          v-model="agree"
          tabindex="0"
          :disabled="!isMounted"
        />
        <span class="ml-2">
          I have read and agree to the
          <nuxt-link
            to="/terms-of-use/employee"
            target="_blank"
            class="underline underline-offset-2"
            >Terms of Use</nuxt-link
          >
          and
          <nuxt-link
            to="https://www.zennioptical.com/privacy-policy"
            target="_blank"
            class="underline underline-offset-2"
            >Privacy Policy</nuxt-link
          >.
        </span>
      </div>
      <ZnButton
        tabindex="0"
        :disabled="!agree || !otpRef.isValidContact"
        :class="`w-full text-center ${
          !agree || !otpRef.isValidContact ? 'bg-grays-light text-white' : ''
        }`"
        @click="handleSetContactAndSendCode()"
      >
        <span v-if="!otpRef?.loading">Submit</span>
        <ZenniIconsIconLoading v-else class="text-white" />
      </ZnButton>
    </div>
  </OtpVerify>
</template>

<script setup lang="ts">
import { jwtDecode } from 'jwt-decode'
import { toast } from '@/components/Toast'
import { type OtpLoginResBody } from '@/types/magicLink'
import { useGiftRepository } from '@/composables/api/repositories/gift'
import { useMounted } from '@vueuse/core'
import { ref, useRouter } from '#imports'
import { useGuestStore } from '~/store/guest'
import { useEmployeeGtmTrackEvent } from '~/composables/useEmployeeGtmTrackEvent'

const emit = defineEmits(['loggedIn'])
const props = defineProps({
  showInDrawer: {
    type: Boolean,
    default: false,
  },
})

const otpRef = ref()
const agree = ref<boolean>()
const loading = ref<boolean>(false)
const guestStore = useGuestStore()
const { loginTrackEvent } = useEmployeeGtmTrackEvent()

const router = useRouter()
const giftApi = useGiftRepository()
const handleVerified = async (data: OtpLoginResBody) => {
  if (data.token) {
    loading.value = true
    const { email } = jwtDecode<{ email: string }>(data.token)

    try {
      const response = await giftApi.execute<'findGift'>(
        giftApi.findGift(email ?? ''),
      )
      if (response) {
        if (props.showInDrawer) {
          emit('loggedIn', { email })
        } else {
          await router.push({
            path: '/',
            query: {
              gift: response.id,
            },
          })
        }
        guestStore.setLoggedInToken(data.token)
        loginTrackEvent(
          guestStore.guest?.userId || '',
          guestStore.gift?.partnerId || '',
        )
      }
    } catch (error) {
      console.error(error)
      toast.open({
        type: 'error',
        title: 'Error occurred',
        content:
          'Failed to load gift information. If the issue persists, contact support.',
      })

      agree.value = false
      otpRef.value?.reset()
    } finally {
      loading.value = false
    }
  }
}

const contact = ref<string>('')

const handleSetContactAndSendCode = () => {
  if (otpRef.value) {
    otpRef.value.handleSendCode()
  }
}
const isMounted = useMounted()
</script>
